import type { ContractItemNavData, WorkOrderNavData, WorkOrderStatusEnum } from 'gql/graphql';
import { ProjectContractTypeEnum, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

const notFrameNameKey = 'Расчет по договору';

/**
 * @param currentOrderId - id Наряд-Заказа
 * @returns {WorkOrderStatusEnum} workOrderStatus - Статус НЗ
 * @returns {string} workOrderTitle - Заголовок НЗ
 * @returns {boolean} hasOtherActsKc2 - Флаг Акт КС2 в Прочих
 * @returns {boolean} isFrameContract - Флаг Рамочный договор
 * @returns {boolean} hasInProgress - Флаг Пред. статус был "В работе"
 * @returns {boolean} isWorkOrderChangeRequired - Флаг Признак необходимости изма НЗ
 * @returns {RequestStatus} projectDataStatus - статус получения данных НЗ
 * @return {() => void} refreshProject - ф-я вызова обновления данных
 * @return {ProjectData} projectData - данные проекта
 * @returns {(WorkOrderNavData|ContractItemNavData)} currentOrder - Данные выбранного НЗ или Расчета
 */
export const useGetBimWorkOrderDataFromProject = ({ currentOrderId }: { currentOrderId?: number }) => {
    const { id: projectId, orderId } = useParams();
    const { projectData, projectDataStatus, refreshProject } = useProjectData(projectId);

    const workOrderId = currentOrderId ?? Number(orderId);

    const isFrameContract = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );

    const currentOrder: WorkOrderNavData | ContractItemNavData | undefined = useMemo(
        () =>
            isFrameContract
                ? projectData?.contractNavigation?.data?.workOrderNavs?.find(
                      (workOrder) => workOrder.id === workOrderId,
                  )
                : projectData?.contractNavigation?.data?.contractNavItems?.find(
                      (item) => item.id === workOrderId || item.name === notFrameNameKey,
                  ),
        [
            isFrameContract,
            projectData?.contractNavigation?.data?.contractNavItems,
            projectData?.contractNavigation?.data?.workOrderNavs,
            workOrderId,
        ],
    );

    const workOrderTitle = useMemo(() => {
        if (!currentOrder) {
            return '';
        }

        return isFrameContract ? getLabelWorkOrder(currentOrder as WorkOrderNavData) : notFrameNameKey;
    }, [currentOrder, isFrameContract]);

    // TODO: добавить проверку для расчета

    const hasWorkOrderChangesValidStatus = useMemo(() => {
        const statusTruest = [WorkOrderChangeStatusEnum.Approving, WorkOrderChangeStatusEnum.OnFormation];
        const isHasStatusTruest = (currentOrder as WorkOrderNavData)?.workOrderChanges?.some(
            (el: { status: WorkOrderChangeStatusEnum }) => {
                const { status } = el;

                return statusTruest.includes(status);
            },
        );

        return isFrameContract ? isHasStatusTruest : false;
    }, [currentOrder, isFrameContract]);

    const {
        status: normalStatus,
        hasActsKC2,
        hasOtherActsKC2: hasOtherActsKc2,
        hasInProgress,
        isWorkOrderChangeRequired,
    } = currentOrder?.metadata ?? {};

    const workOrderStatus: WorkOrderStatusEnum = useMemo(() => {
        if (isFrameContract) {
            return (currentOrder as WorkOrderNavData)?.status;
        }

        return normalStatus;
    }, [currentOrder, isFrameContract, normalStatus]);

    return {
        workOrderStatus,
        workOrderTitle,
        hasOtherActsKc2,
        hasActsKC2,
        hasWorkOrderChangesValidStatus,
        isFrameContract,
        currentOrder,
        isWorkOrderChangeRequired,
        hasInProgress,
        projectDataStatus,
        refreshProject,
        projectData,
    };
};
